import { useEffect, type RefObject } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Keeps Tab focus inside `containerRef` while `enabled`, and restores focus to
 * whatever was focused before on close.
 *
 * Without this a keyboard user tabs straight out of an open drawer into the
 * page behind it, which is both disorienting and lets them operate controls
 * the drawer is meant to be covering.
 */
export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, enabled: boolean): void {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    const previouslyFocused = document.activeElement as HTMLElement | null;

    /* Focus the first control, or the container itself if it holds none. */
    const initial = container.querySelector<HTMLElement>(FOCUSABLE);
    (initial ?? container).focus({ preventScroll: true });

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Tab' || !container) {
        return;
      }

      const focusable = [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (element) => element.offsetParent !== null,
      );

      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!first || !last) {
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus({ preventScroll: true });
    };
  }, [containerRef, enabled]);
}
